import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PIVOTS = [
    { key: "career_shift", label: "Leaving a career, becoming something new", hint: "e.g., corporate → freelance, quitting to write, starting over" },
    { key: "relationship_shift", label: "A relationship is changing shape", hint: "e.g., divorce, breakup, marriage repair, boundary work" },
    { key: "identity_evolution", label: "Rediscovering who I am now", hint: "e.g., after kids, empty nest, post-illness, mid-life" },
    { key: "grief_loss", label: "Moving through grief or loss", hint: "e.g., death, miscarriage, estrangement, the death of a version of me" },
    { key: "motherhood_transition", label: "A motherhood transition", hint: "e.g., new motherhood, returning to work, kids leaving" },
    { key: "other", label: "Something else that doesn't have a name yet", hint: "the space between — bring it as it is" },
];

export default function Onboarding() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [selected, setSelected] = useState(user?.life_pivot || null);
    const [detail, setDetail] = useState(user?.life_pivot_detail || "");
    const [submitting, setSubmitting] = useState(false);

    const handleContinue = async () => {
        if (!selected) {
            toast.error("Choose the one closest to now.");
            return;
        }
        setSubmitting(true);
        try {
            await api.put("/user/profile", {
                life_pivot: selected,
                life_pivot_detail: detail.trim() || null,
                onboarded: true,
            });
            await refreshUser();
            navigate("/dashboard", { replace: true });
        } catch (e) {
            toast.error("Could not save. Try once more.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen cream-grain flex flex-col" data-testid="onboarding-page">
            <header className="max-w-4xl w-full mx-auto px-6 py-6 flex items-center gap-2.5">
                <Flame className="w-5 h-5 text-brand-spark animate-flicker" strokeWidth={1.5} />
                <span className="font-serif text-2xl text-brand-darkPurple tracking-tight">Lantern</span>
            </header>

            <div className="flex-1 max-w-3xl w-full mx-auto px-6 pb-16">
                <div className="mt-6 mb-10">
                    <h1 className="font-serif text-4xl sm:text-5xl text-brand-darkPurple tracking-tight leading-tight">
                        {user?.name ? `Hi, ${user.name}.` : "Hi."} <br className="hidden sm:block" />
                        <span className="italic font-normal text-brand-darkPurple/85">What are you sitting with right now?</span>
                    </h1>
                    <p className="mt-4 text-brand-charcoal/75 text-lg leading-relaxed max-w-2xl">
                        This is just so I can meet you where you are. You can change it any time.
                    </p>
                </div>

                <div className="space-y-2.5">
                    {PIVOTS.map((p) => {
                        const active = selected === p.key;
                        return (
                            <button
                                key={p.key}
                                data-testid={`onboarding-pivot-${p.key}`}
                                onClick={() => setSelected(p.key)}
                                className={`w-full text-left rounded-2xl border p-5 transition-colors duration-200 ${
                                    active
                                        ? "border-brand-darkPurple bg-white shadow-soft-card"
                                        : "border-brand-darkPurple/12 bg-white/60 hover:bg-white hover:border-brand-darkPurple/25"
                                }`}
                            >
                                <div className={`font-serif text-xl ${active ? "text-brand-darkPurple" : "text-brand-darkPurple/85"}`}>{p.label}</div>
                                <div className="text-sm text-brand-charcoal/65 mt-1">{p.hint}</div>
                            </button>
                        );
                    })}
                </div>

                {selected && (
                    <div className="mt-8 animate-fade-in-up">
                        <label className="block font-serif text-xl text-brand-darkPurple mb-3">
                            <em className="italic font-normal">Say a little more — if you want to.</em>
                        </label>
                        <Textarea
                            data-testid="onboarding-detail-input"
                            value={detail}
                            onChange={(e) => setDetail(e.target.value)}
                            placeholder="A sentence, a fragment. Skip if it's too much right now."
                            className="min-h-[110px] bg-white border-brand-darkPurple/15 focus-visible:ring-brand-gold/50 resize-none"
                        />
                    </div>
                )}

                <div className="mt-10 flex justify-end">
                    <Button
                        data-testid="onboarding-continue-btn"
                        onClick={handleContinue}
                        disabled={!selected || submitting}
                        className="bg-brand-darkPurple hover:bg-brand-darkPurpleSoft text-white rounded-full px-7 py-6 text-base"
                    >
                        {submitting ? "Opening…" : "Enter"}
                        <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
